<script setup lang="ts">
import type { CalendarEventDetails, CalendarProvider } from "./utils"
import type { IconKeys } from "@/components/ui/Icon/Icon.interface"
import type { WelcomeInvitation } from "@/models"
import { computed } from "vue"
import { useRoute } from "vue-router"
import { DropdownMenu, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"
import { saveToCalendar } from "./utils"

interface SaveToCalendarProps {
  invitation: WelcomeInvitation
  align?: "start" | "end"
  fullWidth?: boolean
}

const props = withDefaults(defineProps<SaveToCalendarProps>(), {
  align: "start",
  fullWidth: false,
})

const route = useRoute()
const { t } = useTranslation()

const invitationDetails = computed<CalendarEventDetails | null>(() => {
  if (!props.invitation.eventDate)
    return null

  return {
    title: props.invitation.title ?? "",
    description: props.invitation.message ?? "",
    startDate: props.invitation.eventDate,
    endDate: props.invitation.eventEndDate,
    timezone: props.invitation.timezone,
    isAllDay: !props.invitation.timezone,
    location: props.invitation.location?.formattedAddress ?? props.invitation.location?.displayAddress,
    url: `${window.location.origin}${route.fullPath}`,
  }
})

const calendarProviderItems = computed(() => {
  const details = invitationDetails.value
  if (!details)
    return []

  const providers: {
    key: CalendarProvider
    label: string
    icon: IconKeys
  }[] = [
    { key: "google", label: t("save_to_calendar.google"), icon: "Google" },
    { key: "outlook", label: t("save_to_calendar.outlook"), icon: "Outlook" },
    { key: "ical", label: t("save_to_calendar.ical"), icon: "Calendar" },
  ]

  return providers.map(({ key, label, icon }) => ({
    key,
    label,
    icon,
    onClick: () => saveToCalendar(key, details),
    dismissOnInteract: true,
  }))
})
</script>

<template>
  <DropdownMenu
    v-if="invitationDetails"
    :items="calendarProviderItems"
    side="bottom"
    :align="align"
    :trigger-class="fullWidth ? 'flex w-full min-w-0 max-w-full' : undefined"
    data-test="save-to-calendar-menu"
  >
    <template #activator="{ isOpen }">
      <slot name="activator" :is-open="isOpen">
        <button
          type="button"
          class="mt-1 flex w-fit cursor-pointer items-center gap-1.5 text-xs text-secondary-card-text/80 underline-offset-2 hover:text-primary hover:underline"
          data-test="save-to-calendar-trigger"
          :aria-expanded="isOpen"
          aria-haspopup="menu"
        >
          {{ t("save_to_calendar.menu_title") }}
          <Icon icon="CalendarPlus" class="shrink-0" size="xs" />
        </button>
      </slot>
    </template>
  </DropdownMenu>
</template>
